import {
  WorkflowConditionGroupDto,
  WorkflowConditionLeafDto,
  WorkflowEmailConfigDto,
  WorkflowFieldDataTypeDto,
  WorkflowFieldDto,
  WorkflowFieldSettingsDto,
  WorkflowGraphDto,
  WorkflowKeyValueDto,
  WorkflowPauseConfigDto,
  WorkflowStageDto,
  WorkflowTaskConfigDto,
  WorkflowTransitionDto,
  WorkflowWebserviceConfigDto,
} from "../../types/workflowDto";
import {
  createSeedCanvas,
  createWorkflowApprovalConfig,
  isFieldPersonSource,
  WorkflowCanvas,
  WorkflowConditionOperator,
  WorkflowConditionPath,
  WorkflowFieldDataType,
  WorkflowFormField,
  WorkflowKeyValue,
  WorkflowWebServiceConfig,
} from "./workflowBuilderData";
import {
  approvalFromConfig,
  asString,
  cancelFromConfig,
  emailFromConfig,
  fromAssignModel,
  fromResolverSource,
  JsonObject,
  parseJsonObject,
  pauseUnitFromApi,
} from "./workflowStageConfigJson";
import { WorkflowDefinition, WorkflowDefinitionStatus } from "./workflowDefinitionsData";
import { isPifDefinitionToken } from "./pifWorkflowKeys";

// ---------------------------------------------------------------------------
// Maps GET api/workflow/definition's graph onto the WorkflowDefinition / WorkflowCanvas shape the
// runtime stages already render from, so the stage components stay unchanged. Canvas ids are the
// numeric stage ids as strings — the same value advance / save-values report back.
//
// ConfigJson is parsed defensively: a malformed or missing config yields that canvas's defaults
// rather than throwing, because one mis-built stage must not blank the whole Run screen.
//
// Read-only and Run-screen only. The Builder's load/save round trip is workflowGraphMapper.ts
// (Epic 80429), whose canvas ids are `wf-stage-{id}` client ids rather than the bare stage ids the
// runtime reports back, so the two are kept apart. They share the ConfigJson vocabulary
// (workflowStageConfigJson.ts), so a stage reads the same in both.
// ---------------------------------------------------------------------------

const parseJson = <T extends object>(json: string | null | undefined): Partial<T> => {
  if (!json) return {};
  try {
    const parsed: unknown = JSON.parse(json);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Partial<T>) : {};
  } catch {
    return {};
  }
};

/** Ascending SortVal, nulls last, then Id — the order the engine itself evaluates in. */
const bySortVal = <T extends { id: number; sortVal: number | null }>(a: T, b: T): number =>
  (a.sortVal ?? Number.MAX_SAFE_INTEGER) - (b.sortVal ?? Number.MAX_SAFE_INTEGER) || a.id - b.id;

const stageRef = (stageId: number | null | undefined): string =>
  stageId === null || stageId === undefined ? "" : String(stageId);

/** `userpicker` and `datetime` have no runtime renderer yet; they fall back to the closest one. */
const FIELD_DATA_TYPES: Record<WorkflowFieldDataTypeDto, WorkflowFieldDataType> = {
  text: "text",
  richtext: "richtext",
  dropdown: "dropdown",
  userpicker: "text",
  date: "date",
  datetime: "date",
  number: "number",
  boolean: "boolean",
  file: "file",
};

const CONDITION_OPERATORS: Record<string, WorkflowConditionOperator> = {
  eq: "is",
  ne: "is-not",
  contains: "contains",
  gt: "greater-than",
  gte: "greater-than",
  lt: "less-than",
  lte: "less-than",
  isnull: "is-empty",
};

const WEB_SERVICE_METHODS: WorkflowWebServiceConfig["method"][] = ["GET", "POST", "PUT", "PATCH", "DELETE"];

const DEFINITION_STATUSES: WorkflowDefinitionStatus[] = ["published", "draft", "archived"];

const toConditionValue = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.map(toConditionValue).join(", ");
  return String(value);
};

const isConditionLeaf = (rule: WorkflowConditionLeafDto | WorkflowConditionGroupDto): rule is WorkflowConditionLeafDto =>
  "var" in rule;

const toKeyValues = (prefix: string, pairs: WorkflowKeyValueDto[] | undefined): WorkflowKeyValue[] =>
  (pairs ?? []).map((pair, index) => ({ id: `${prefix}-${index}`, key: pair.key, value: pair.value }));

const toFormField = (field: WorkflowFieldDto, graph: WorkflowGraphDto): WorkflowFormField => {
  const settings = parseJson<WorkflowFieldSettingsDto>(field.settingsJson);
  return {
    id: String(field.id),
    fieldKey: field.fieldKey?.trim() || undefined,
    label: field.label,
    dataType: FIELD_DATA_TYPES[field.dataType] ?? "text",
    required: field.isRequired,
    readOnly: field.isReadOnly,
    visibility: field.visibilityRule?.toLowerCase() === "hide" ? "hide" : "show",
    help: field.helpUrl ?? "",
    // v1 has no tree fields, so only top-level options are listed.
    options: graph.fieldOptions
      .filter((option) => option.workflowFieldId === field.id && option.parentOptionId === null)
      .sort(bySortVal)
      .map((option) => option.name),
    settings: {
      min: settings.minChars ?? settings.min ?? null,
      max: settings.maxChars ?? settings.max ?? null,
      rows: settings.rows ?? null,
      maxSize: settings.maxSizeMb ?? null,
      accept: (settings.accept ?? []).join(", "),
    },
  };
};

const toConditionPaths = (transitions: WorkflowTransitionDto[]): WorkflowConditionPath[] =>
  transitions
    .filter((transition) => transition.gate === "path")
    .sort(bySortVal)
    .map((transition) => {
      const condition = parseJson<WorkflowConditionGroupDto>(transition.conditionJson);
      return {
        id: `path-${transition.id}`,
        name: transition.pathLabel ?? "",
        logic: condition.op?.toUpperCase() === "OR" ? "OR" : "AND",
        // Nested groups are evaluated by the engine but have no row in the read-only route panel.
        criteria: (condition.rules ?? []).filter(isConditionLeaf).map((rule, index) => ({
          id: `criterion-${transition.id}-${index}`,
          variable: rule.var,
          operator: CONDITION_OPERATORS[(rule.cmp ?? "").toLowerCase()] ?? "is",
          value: toConditionValue(rule.val),
        })),
        routeId: stageRef(transition.toStageId),
      };
    });

/** Same reading as the Builder's mapper: an OwnerRule the resolver doesn't know is "not set". */
const toOwner = (stage: WorkflowStageDto, config: JsonObject): Pick<WorkflowCanvas, "ownerSource" | "owner"> => {
  const ownerSource = fromResolverSource(stage.ownerRule) ?? "";
  if (ownerSource === "specific-user") {
    return { ownerSource, owner: stage.ownerUserId ? String(stage.ownerUserId) : "" };
  }
  if (isFieldPersonSource(ownerSource)) {
    return { ownerSource, owner: asString(config.ownerFieldKey) };
  }
  return { ownerSource, owner: "" };
};

/** A pause's `route` is a PathLabel; null or unmatched falls back to the default gate. */
const toPauseRouteId = (route: string | null | undefined, transitions: WorkflowTransitionDto[]): string => {
  const routed = route
    ? transitions.find((transition) => transition.pathLabel?.toLowerCase() === route.toLowerCase())
    : undefined;
  return stageRef((routed ?? transitions.find((transition) => transition.gate === "default"))?.toStageId);
};

const toCanvas = (stage: WorkflowStageDto, graph: WorkflowGraphDto): WorkflowCanvas => {
  const outgoing = graph.transitions.filter((transition) => transition.fromStageId === stage.id);
  const gateRoute = (gate: WorkflowTransitionDto["gate"]) =>
    stageRef(outgoing.find((transition) => transition.gate === gate)?.toStageId);
  const rawConfig = parseJsonObject(stage.configJson);
  const canvas = createSeedCanvas({
    id: String(stage.id),
    // "" = the server returned no key (WorkflowCanvas.stageKey); stageKeyOf falls back to the id.
    stageKey: stage.stageKey?.trim() ?? "",
    type: stage.stageType,
    name: stage.name,
    summary: stage.summary ?? "",
    ...toOwner(stage, rawConfig),
    fields: graph.fields
      .filter((field) => field.workflowStageId === stage.id)
      .sort(bySortVal)
      .map((field) => toFormField(field, graph)),
  });

  switch (stage.stageType) {
    case "approval":
      // The approver's name comes from GET approvals at runtime, not from the definition.
      canvas.approval = createWorkflowApprovalConfig({
        ...approvalFromConfig(rawConfig, canvas.approval),
        acceptRouteId: gateRoute("accept"),
        rejectRouteId: gateRoute("reject"),
        cancelRouteId: gateRoute("cancel"),
      });
      break;
    case "conditional":
      canvas.paths = toConditionPaths(outgoing);
      break;
    case "task": {
      const config = parseJson<WorkflowTaskConfigDto>(stage.configJson);
      canvas.tasks = (config.tasks ?? []).map((task) => ({
        id: task.taskKey,
        title: task.title ?? "",
        description: task.description ?? "",
        assigneeSource: fromAssignModel(task.assignModel) ?? "specific-user",
        assignee: task.assigneeRef ?? "",
      }));
      break;
    }
    case "webservice": {
      const config = parseJson<WorkflowWebserviceConfigDto>(stage.configJson);
      const method = config.method?.toUpperCase() as WorkflowWebServiceConfig["method"] | undefined;
      canvas.webService = {
        method: method && WEB_SERVICE_METHODS.includes(method) ? method : "GET",
        url: config.url ?? "",
        onFailure: config.onFailure === "cancel" ? "cancel" : "continue",
        params: toKeyValues(`param-${stage.id}`, config.params),
        headers: toKeyValues(`header-${stage.id}`, config.headers),
        captures: (config.responseCaptures ?? []).map((capture, index) => ({
          id: `capture-${stage.id}-${index}`,
          responseField: capture.path,
          variable: capture.var,
        })),
      };
      break;
    }
    case "email": {
      // The Builder edits one recipient; the Run preview lists every address the step sends to.
      const recipients = (parseJson<WorkflowEmailConfigDto>(stage.configJson).to ?? []).join(", ");
      canvas.email = { ...emailFromConfig(rawConfig), ...(recipients ? { recipient: recipients } : {}) };
      break;
    }
    case "pause": {
      const config = parseJson<WorkflowPauseConfigDto>(stage.configJson);
      canvas.pause = {
        amount: config.amount ?? 0,
        // The Builder leaves an unknown unit unset for Validate; at runtime the wait still shows.
        unit: pauseUnitFromApi(config.unit) || "days",
        routeId: toPauseRouteId(config.route, outgoing),
      };
      break;
    }
    case "cancel":
      canvas.cancel = cancelFromConfig(rawConfig);
      break;
    default:
      break;
  }

  return canvas;
};

/** Stage order for the pipeline rail: the start stage first, then SortVal. */
const byPipelineOrder = (a: WorkflowStageDto, b: WorkflowStageDto): number =>
  Number(b.isStart) - Number(a.isStart) || bySortVal(a, b);

export const mapWorkflowGraphToDefinition = (graph: WorkflowGraphDto): WorkflowDefinition => {
  const { definition } = graph;
  const status = definition.status.toLowerCase() as WorkflowDefinitionStatus;
  const canvases = [...graph.stages].sort(byPipelineOrder).map((stage) => toCanvas(stage, graph));
  return {
    id: definition.id,
    name: definition.name,
    category: definition.category ?? "",
    status: DEFINITION_STATUSES.includes(status) ? status : "draft",
    description: definition.description ?? "",
    version: definition.versionNo,
    // Not part of the graph; the Run screen never shows it.
    activeInstanceCount: 0,
    // rowVersionTag is an opaque concurrency token and must not be parsed for a date.
    editedOn: "",
    // The PIF is recognised by its definition Token, never by name, position or stage keys (a
    // copy of the PIF carries the same stage keys but never the PIF's Token).
    isFlagship: isPifDefinitionToken(definition.token),
    canvases,
  };
};
